import { c as _c } from "react/compiler-runtime";
import { DateFilterChip } from "@components/common/faro-table/faro-table-filter/date-filter/date-filter-chip";
import { DropdownFilterChip } from "@components/common/faro-table/faro-table-filter/dropdown-filter-chip";
import { TEAMS_TABLE_COLUMN_LABELS } from "@components/table/teams/teams-table-constants";
import { TeamsMembersFilterChip } from "@components/teams/teams-filters/members-filter/teams-members-filter-chip";
import { useTeamsOfFilterMembersFetched } from "@components/teams/teams-filters/members-filter/use-teams-of-filter-members";
import { TEAMS_CREATED_FILTER_COLUMN, TEAMS_FILTER_COLUMNS, TEAMS_MEMBERS_FILTER_COLUMN, TEAMS_ROLE_FILTER_COLUMN } from "@components/teams/teams-filters/teams-filter-constants";
import { getActiveTeamsFilterIds, getTeamsCreatedFilterPresets, getTeamsFilterOptions, getTeamsRoleFilterOptions } from "@components/teams/teams-filters/teams-filter-options";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { doesColumnPassAnyFilter } from "@hooks/table-filters/use-apply-filter-utils";
import { useFilterQueryParams } from "@hooks/table-filters/use-filter-query-params";
import { FaroCoreApiConst } from "@stellar/core-api-client";
import { fetchingSdbCompanyFlagsSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { setTableType } from "@store/table/table-slice";
import { teamIdsByMemberSelector } from "@store/teams/teams-selector";
import { TableType } from "@utils/track-event/track-event-list";
import { getAssignableWorkspaceRoles } from "@utils/workspace-role-utils";
import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/** The filters the user applied to a column, which the column offers */

/**
 * Applies the members, creation date and workspace role filters to the teams and returns the chips
 * to change them. The teams are all fetched at once, so they are filtered here. To filter by
 * specific members, the teams of each of them are fetched once, see the teams slice.
 */
export function useTeamsFilters() {
  const $ = _c(20);
  const {
    getFilterIdsForColumn
  } = useFilterQueryParams();
  const roles = useTeamsFilterRoles();
  const teamIdsByMember = useAppSelector(teamIdsByMemberSelector);
  const context = {
    roles,
    teamIdsByMember
  };
  const dispatch = useAppDispatch();
  let t0;
  let t1;
  if ($[0] !== dispatch) {
    t0 = () => {
      dispatch(setTableType(TableType.teams));
    };
    t1 = [dispatch];
    $[0] = dispatch;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  const filtersByColumn = TEAMS_FILTER_COLUMNS.map(column => {
    const filterIds = getFilterIdsForColumn(column);
    return {
      column,
      filterIds,
      activeFilterIds: getActiveTeamsFilterIds(column, filterIds, context)
    };
  });
  const isAnyFilterApplied = filtersByColumn.some(_temp);
  const hasIgnoredFilterIds = filtersByColumn.some(_temp2);
  useRemoveIgnoredFilterIds(hasIgnoredFilterIds, context);
  const {
    isLoading: isLoadingFilters
  } = useTeamsOfFilterMembersFetched(filtersByColumn.find(_temp3)?.activeFilterIds ?? []);
  const activeFiltersKey = JSON.stringify(filtersByColumn.flatMap(_temp4));
  let t2;
  if ($[3] !== roles) {
    t2 = JSON.stringify(roles);
    $[3] = roles;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const rolesKey = t2;
  let t3;
  if ($[5] !== activeFiltersKey || $[6] !== rolesKey || $[7] !== teamIdsByMember) {
    t3 = teams => {
      const activeFilters = JSON.parse(activeFiltersKey);
      if (activeFilters.length === 0) {
        return teams;
      }
      const filterContext = {
        roles: JSON.parse(rolesKey),
        teamIdsByMember
      };
      const columnFilters = activeFilters.map(t4 => {
        const [column_2, activeFilterIds_2] = t4;
        return {
          activeFilterIds: activeFilterIds_2,
          allPossibleFiltersForColumn: getTeamsFilterOptions(column_2, activeFilterIds_2, filterContext)
        };
      });
      return teams.filter(team => columnFilters.every(columnFilter => doesColumnPassAnyFilter({
        entity: team,
        ...columnFilter
      })));
    };
    $[5] = activeFiltersKey;
    $[6] = rolesKey;
    $[7] = teamIdsByMember;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const filterTeams = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TeamsMembersFilterChip key={TEAMS_MEMBERS_FILTER_COLUMN} />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DateFilterChip key={TEAMS_CREATED_FILTER_COLUMN} label={TEAMS_TABLE_COLUMN_LABELS.creationDate} filteredColumn={TEAMS_CREATED_FILTER_COLUMN} presets={getTeamsCreatedFilterPresets()} tableType={TableType.teams} />;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== roles) {
    t6 = getTeamsRoleFilterOptions(roles);
    $[11] = roles;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = {
      [TEAMS_MEMBERS_FILTER_COLUMN]: t4,
      [TEAMS_CREATED_FILTER_COLUMN]: t5,
      [TEAMS_ROLE_FILTER_COLUMN]: <DropdownFilterChip key={TEAMS_ROLE_FILTER_COLUMN} label={TEAMS_TABLE_COLUMN_LABELS.workspaceRole} filteredColumn={TEAMS_ROLE_FILTER_COLUMN} items={t6} shouldShowSearchBox={false} shouldShowCheckbox={true} shouldAllowLabelTranslation={true} tableType={TableType.teams} />
    };
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const filterChips = t7;
  let t8;
  if ($[15] !== filterChips || $[16] !== filterTeams || $[17] !== isAnyFilterApplied || $[18] !== isLoadingFilters) {
    t8 = {
      isAnyFilterApplied,
      isLoadingFilters,
      filterTeams,
      filterChips
    };
    $[15] = filterChips;
    $[16] = filterTeams;
    $[17] = isAnyFilterApplied;
    $[18] = isLoadingFilters;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}

/**
 * Returns the workspace roles the teams can be filtered by, which leave out the manager roles in
 * workspaces that cannot assign managers. Until the features of the workspace are fetched, all the
 * roles are offered, so that a role applied from a link is not removed too early.
 */
function _temp4(t0) {
  const {
    column: column_1,
    activeFilterIds: activeFilterIds_1
  } = t0;
  return activeFilterIds_1.length === 0 ? [] : [[column_1, activeFilterIds_1]];
}
function _temp3(t0) {
  const {
    column: column_0
  } = t0;
  return column_0 === TEAMS_MEMBERS_FILTER_COLUMN;
}
function _temp2(t0) {
  const {
    filterIds: filterIds_0,
    activeFilterIds: activeFilterIds_0
  } = t0;
  return filterIds_0.length > activeFilterIds_0.length;
}
function _temp(t0) {
  const {
    activeFilterIds
  } = t0;
  return activeFilterIds.length > 0;
}
function useTeamsFilterRoles() {
  const $ = _c(3);
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const {
    fetchSelectedCompanyContext
  } = useAppSelector(fetchingSdbCompanyFlagsSelector);
  let t0;
  if ($[0] !== canAssignManagers || $[1] !== fetchSelectedCompanyContext) {
    t0 = fetchSelectedCompanyContext === "succeeded" ? getAssignableWorkspaceRoles("invite", canAssignManagers) : [...FaroCoreApiConst.allCompanyRoles];
    $[0] = canAssignManagers;
    $[1] = fetchSelectedCompanyContext;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}

/**
 * Removes the IDs the filters ignore from the URL, e.g. from an old link, since the chips and the
 * toolbar read the query params too
 */
function useRemoveIgnoredFilterIds(hasIgnoredFilterIds, context) {
  const $ = _c(7);
  const [, setSearchParams] = useSearchParams();
  let t0;
  if ($[0] !== context.roles) {
    t0 = JSON.stringify(context.roles);
    $[0] = context.roles;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const rolesKey = t0;
  let t1;
  let t2;
  if ($[2] !== hasIgnoredFilterIds || $[3] !== rolesKey || $[4] !== setSearchParams) {
    t1 = () => {
      if (!hasIgnoredFilterIds) {
        return;
      }
      const filterContext = {
        roles: JSON.parse(rolesKey),
        teamIdsByMember: {}
      };
      setSearchParams(searchParams => {
        TEAMS_FILTER_COLUMNS.forEach(column => {
          const activeFilterIds = getActiveTeamsFilterIds(column, searchParams.getAll(column), filterContext);
          searchParams.delete(column);
          activeFilterIds.forEach(filterId => searchParams.append(column, filterId));
        });
        return searchParams;
      }, {
        replace: true
      });
    };
    t2 = [hasIgnoredFilterIds, rolesKey, setSearchParams];
    $[2] = hasIgnoredFilterIds;
    $[3] = rolesKey;
    $[4] = setSearchParams;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
}